import { AlertTriangle, Check, X } from 'lucide-react'
import { useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useOffboarding, type OffboardingInput, useActiveEmployees, type Employee } from '@/hooks'
import { cn } from '@/lib/utils'

interface OffboardingWizardProps {
  employee: Employee
  pendingTasks?: Array<{ id: string; title: string; projectName: string }>
  onClose: () => void
  onSuccess?: () => void
}

const STEPS = [
  { id: 1, title: '离职信息', description: '离职日期和原因' },
  { id: 2, title: '任务交接', description: '待办任务交接' },
  { id: 3, title: '确认离职', description: '确认并完成' },
]

export function OffboardingWizard({ employee, pendingTasks = [], onClose, onSuccess }: OffboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const offboardingMutation = useOffboarding()
  const { data: activeEmployees } = useActiveEmployees()

  const [resignInfo, setResignInfo] = useState({
    resignDate: new Date().toISOString().split('T')[0],
    reason: '',
  })

  const [taskHandover, setTaskHandover] = useState<Record<string, string>>({})
  const [confirmed, setConfirmed] = useState(false)

  const handleNext = () => setCurrentStep((prev) => Math.min(prev + 1, STEPS.length))
  const handlePrev = () => setCurrentStep((prev) => Math.max(prev - 1, 1))

  const handleSubmit = () => {
    if (!confirmed) { return }

    const data: OffboardingInput = {
      resignDate: resignInfo.resignDate,
      reason: resignInfo.reason || undefined,
      taskHandover,
      sendNotification: true,
    }

    offboardingMutation.mutate(
      { id: employee.id, data },
      { onSuccess: () => { onSuccess?.(); onClose() } }
    )
  }

  // Filter out the resigning employee from handover options
  const handoverOptions = activeEmployees?.filter((e) => e.id !== employee.id) ?? []

  const renderResignInfoStep = () => (
    <div className="space-y-4">
      <div className="rounded-md bg-yellow-50 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600" />
          <div>
            <p className="font-medium text-yellow-800">即将为 {employee.name} 办理离职</p>
            <p className="text-sm text-yellow-700">离职后，员工将无法登录系统，团队成员关系将被归档。</p>
          </div>
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium">离职日期 <span className="text-destructive">*</span></label>
        <input
          type="date"
          value={resignInfo.resignDate}
          onChange={(e) => setResignInfo({ ...resignInfo, resignDate: e.target.value })}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium">离职原因</label>
        <textarea
          value={resignInfo.reason}
          onChange={(e) => setResignInfo({ ...resignInfo, reason: e.target.value })}
          placeholder="请输入离职原因（可选）"
          rows={3}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
    </div>
  )


  const renderTaskHandoverStep = () => (
    <div className="space-y-4">
      {pendingTasks.length === 0 ? (
        <div className="rounded-md bg-muted p-4 text-center">
          <p className="text-sm text-muted-foreground">该员工没有待办任务需要交接</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            以下是该员工的待办任务，请为每个任务选择交接人：
          </p>
          <div className="space-y-3">
            {pendingTasks.map((task) => (
              <div key={task.id} className="rounded-md border p-3">
                <div className="mb-2">
                  <p className="font-medium">{task.title}</p>
                  <p className="text-sm text-muted-foreground">{task.projectName}</p>
                </div>
                <Select
                  value={taskHandover[task.id] || 'placeholder'}
                  onValueChange={(value) => setTaskHandover({ ...taskHandover, [task.id]: value === 'placeholder' ? '' : value })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="不指定交接人（取消分配）" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="placeholder">不指定交接人（取消分配）</SelectItem>
                    {handoverOptions.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )

  const renderConfirmStep = () => (
    <div className="space-y-4">
      <div className="rounded-md border p-4">
        <h4 className="mb-3 font-medium">离职摘要</h4>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">员工姓名</dt>
            <dd className="font-medium">{employee.name}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">员工编号</dt>
            <dd className="font-medium">{employee.employeeNo}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">离职日期</dt>
            <dd className="font-medium">{resignInfo.resignDate}</dd>
          </div>
          {resignInfo.reason && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">离职原因</dt>
              <dd className="font-medium">{resignInfo.reason}</dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-muted-foreground">待交接任务</dt>
            <dd className="font-medium">{pendingTasks.length} 个</dd>
          </div>
        </dl>
      </div>

      <div className="rounded-md bg-destructive/10 p-4">
        <p className="text-sm text-destructive">
          离职操作将执行以下变更：
        </p>
        <ul className="mt-2 list-inside list-disc text-sm text-destructive">
          <li>员工状态变更为&quot;已离职&quot;</li>
          <li>禁用员工登录权限</li>
          <li>归档所有团队成员关系</li>
          <li>取消待审批的请假申请</li>
          <li>升级员工作为审批人的待审批项</li>
        </ul>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="confirm"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300"
        />
        <label htmlFor="confirm" className="text-sm">
          我已确认以上信息，同意执行离职操作
        </label>
      </div>
    </div>
  )

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return renderResignInfoStep()
      case 2: return renderTaskHandoverStep()
      case 3: return renderConfirmStep()
      default: return null
    }
  }


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg max-h-[90vh] overflow-hidden rounded-lg bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">员工离职向导</h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium',
                      currentStep > step.id
                        ? 'bg-primary text-primary-foreground'
                        : currentStep === step.id
                          ? 'border-2 border-primary text-primary'
                          : 'border-2 border-muted text-muted-foreground'
                    )}
                  >
                    {currentStep > step.id ? <Check className="h-4 w-4" /> : step.id}
                  </div>
                  <span className={cn('mt-1 text-xs', currentStep >= step.id ? 'text-foreground' : 'text-muted-foreground')}>
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={cn('mx-4 h-0.5 w-16', currentStep > step.id ? 'bg-primary' : 'bg-muted')} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="max-h-[50vh] overflow-y-auto p-6">
          {renderStepContent()}
          {offboardingMutation.error && (
            <div className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {offboardingMutation.error.message}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t px-6 py-4">
          <button
            type="button"
            onClick={handlePrev}
            disabled={currentStep === 1}
            className="rounded-md px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            上一步
          </button>

          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium hover:bg-muted">
              取消
            </button>

            {currentStep < STEPS.length ? (
              <button
                type="button"
                onClick={handleNext}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                下一步
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!confirmed || offboardingMutation.isPending}
                className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
              >
                {offboardingMutation.isPending ? '处理中...' : '确认离职'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
